import type { FocusEvent, InputHTMLAttributes } from 'react'
import { useState } from 'react'
import styled, { CSSProperties } from 'styled-components'
import { sc } from 'utils'

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  touched?: boolean
  invalid?: boolean
}

export default function Input(props: InputProps) {
  const [touched, setTouched] = useState(false)

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    setTouched(true)
    props.onFocus?.(event)
  }

  return (
    <StyledInput
      {...props}
      touched={props.touched ?? touched}
      invalid={props.invalid ?? false}
      onFocus={handleFocus}
    />
  )
}

const StyledInput = styled.input<{ touched: boolean; invalid: boolean }>`
  padding: 15px 16px;

  background: white;
  border: none;
  outline: 1px solid var(--light-gray);
  outline-offset: -1px;
  border-radius: 4px;

  color: inherit;
  caret-color: var(--green);
  font: var(--400-13);
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: all 0.2s;
  ${sc('invalid', 'outline-color: var(--red);')}

  :focus {
    outline-color: var(--gray);
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  }

  :valid:not(:focus) {
    ${sc('touched', 'outline-color: var(--green);')}
  }

  :disabled {
    background: var(--light-gray);
    cursor: not-allowed;
  }
`

type Props = {
  prefix: string
  reverse?: boolean
  prefixWidth?: string
} & InputProps

export function PrefixedInput({
  prefix,
  reverse,
  prefixWidth = '43px',
  style,
  className,
  ...props
}: Props) {
  return (
    <StyledPrefixedInput
      reverse={reverse}
      style={{ ...style, '--prefixWidth': prefixWidth } as CSSProperties}
      className={className}
    >
      <span>{prefix}</span>
      <Input {...props} />
    </StyledPrefixedInput>
  )
}

export const StyledPrefixedInput = styled.div<{ reverse: Props['reverse'] }>`
  display: flex;
  align-items: center;
  position: relative;
  ${sc('reverse', 'flex-direction: row-reverse;')}

  > span {
    flex: none;
    padding: 15px 16px;
    position: relative;
    z-index: 1;
    font: var(--400-13);
    line-height: 18px;
    pointer-events: none;
  }

  > ${StyledInput} {
    flex: 1;
    position: absolute;
    top: 1px;
    left: 1px;
    bottom: 1px;
    right: 1px;
    z-index: 0;
    ${sc('reverse', 'padding-right: var(--prefixWidth);', 'padding-left: var(--prefixWidth);')}
  }
`
